"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.isFacilityRequiringCANodeType = exports.FACILITY_LESS_ALLOWED_CA_TYPES = exports.isCANodeType = exports.isPrimitiveNodeType = exports.PRIMITIVE_ARTIFACT_TYPES = void 0;
/**
 * Node-layer partition over `DbTable.ArtifactType`.
 *
 * The graph splits into two layers (see
 * `docs/BBPL_Graph_Primitives_Decouple.md`):
 *
 * - **primitive** (domain-agnostic): a node whose task runs purely from node
 *   `configuration` + input buffers + output-routing scalars. It knows nothing
 *   about credit agreements / facilities. This is the reusable substrate the CA
 *   domain — and, later, the BUS agent — build on.
 * - **CA** (credit-agreement / credit-facility): a node that reads CA mechanics
 *   or asserts a credit facility. Consumes the primitive layer.
 *
 * `CAArtifactType` is derived as the **complement** of `PrimitiveArtifactType`,
 * so the two layers are exhaustive and disjoint over `ArtifactType` *by
 * construction* — a new artifact type is CA until it is explicitly listed here.
 * This module is the single source of truth for that partition; both the BBPL
 * executor registry and commit-time graph validation import it.
 */
// Preserves the literal tuple type while enforcing every member is a valid
// `ArtifactType` (equivalent to `satisfies readonly ArtifactType[]`, but
// compatible with the repo's pinned prettier 2.2.1 which predates `satisfies`).
const asArtifactTypes = (types) => types;
/**
 * The primitive (domain-agnostic) artifact types. Adding a type here moves it
 * from the CA layer into the primitive layer — do so only when its task truly
 * needs no credit-agreement data.
 */
exports.PRIMITIVE_ARTIFACT_TYPES = asArtifactTypes([
    'userInputNeeded',
    'rawReceivableDocumentation',
    'rawLoanTape',
    'rawCashBalanceFeed',
    'rawRepaymentTape',
    'rawSupplementalTape',
    'rawZipTape',
    'configTape',
    'generatedSupplementalTape',
    'intermediateTape',
    'generatedLoanTape',
    'generatedCashBalanceFeed',
    'generatedRepaymentTape',
    'finStatement',
    'cashBalancesFeedContext',
    'fundingApprovalReport',
    'forwardedTape',
    'orgProvidedInput',
    'orgProvidedInputs',
    'uploadToDatalake',
    'waitForDataLakeTable',
]);
const PRIMITIVE_ARTIFACT_TYPE_SET = new Set(exports.PRIMITIVE_ARTIFACT_TYPES);
/**
 * True if the artifact type belongs to the primitive (domain-agnostic) layer.
 * Narrows to {@link PrimitiveArtifactType} so callers can dispatch to the
 * primitive registry / accept the node into a primitives-only graph.
 */
const isPrimitiveNodeType = (type) => PRIMITIVE_ARTIFACT_TYPE_SET.has(type);
exports.isPrimitiveNodeType = isPrimitiveNodeType;
/**
 * True if the artifact type belongs to the CA (credit-agreement) layer.
 * Narrows to {@link CAArtifactType}. The exact negation of
 * {@link isPrimitiveNodeType}.
 */
const isCANodeType = (type) => !(0, exports.isPrimitiveNodeType)(type);
exports.isCANodeType = isCANodeType;
/**
 * CA node types that are nonetheless ALLOWED inside a primitives-only
 * (facility-less) graph. These bind their credit facilities at run time from
 * the ingested data (e.g. Valley Bank SLS maps CSV participants → facilities
 * via `accessors.creditFacility`), so their graph nodes legitimately carry no
 * node-level `creditFacilityId` and the document has an empty `cfIdToSlugMap`.
 *
 * They are classified CA (their tasks touch facility data), but the
 * facility-less-graph guardrail must not reject them — a real committed graph
 * (the "Valley Bank - SLS Borrowers" ingest pipeline) has exactly this shape.
 * See `docs/BBPL_Graph_Primitives_Decouple.md`.
 */
exports.FACILITY_LESS_ALLOWED_CA_TYPES = new Set(['valleyBankIngest', 'valleyBankSlsIngest', 'valleyBankSlsCommitment']);
/**
 * True if `type` is a CA node type that the facility-less-graph guardrail must
 * still reject (i.e. CA and not in {@link FACILITY_LESS_ALLOWED_CA_TYPES}).
 */
const isFacilityRequiringCANodeType = (type) => (0, exports.isCANodeType)(type) && !exports.FACILITY_LESS_ALLOWED_CA_TYPES.has(type);
exports.isFacilityRequiringCANodeType = isFacilityRequiringCANodeType;
//# sourceMappingURL=nodeLayer.js.map